import "./Master.scss";

import { SvgIcon } from "@mui/material";
import ButtonField from "../../Components/Button/ButtonField";
import Table from "../../Components/Table/Table";
import CustomToolbar from "../../Components/Table/components/CustomToolbar";
import TableSearch from "../../Components/Table/components/TableSearch";
import { IMasterList, MastersProps } from "./IMaster";
import {
  FunctionComponent,
  MouseEvent,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { ReactComponent as Add } from "../../Assets/Images/Add.svg";
import { Columns } from "../../Components/Table/ITable";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { getMaster } from "../../Redux/Slicers/Master/MasterSlicer";
import {
  GridActionsCellItem,
  GridRowParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { ReactComponent as Edit } from "../../Assets/Images/Edit.svg";
import { ReactComponent as Delete } from "../../Assets/Images/Delete.svg";
import Dialog from "../../Components/Dialog/Dialog";
import IconButtonField from "../../Components/Button/IconButtonField";
import { ReactComponent as Cancel } from "../../Assets/Images/Cancel.svg";
import MasterForm from "./MasterForm";
import { FormikProps } from "formik";
import {
  clearUpdateMasterState,
  mastersUpdateableData,
} from "../../Redux/Slicers/Master/UpdateMasterSlicer";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";
import DeleteDialog from "../../Components/DeleteDialog/DeleteDialog";
import {
  clearDeleteMaster,
  deleteMaster,
} from "../../Redux/Slicers/Master/DeleteMasterSlice";
import {
  clearUnlockMaster,
  unlockMaster,
} from "../../Redux/Slicers/Master/UnlockMasterSlice";
import { clearAddMasterState } from "../../Redux/Slicers/Master/AddMasterSlicer";
import { LockOpen } from "@mui/icons-material";
import FailureResponseDialog from "../../Components/FailureResponseDialog/FailureResponseDialog";

const Master: FunctionComponent<MastersProps> = () => {
  const dispatch = useAppDispatch();
  const { data } = useAppSelector((state) => state.Master);
  const [open, setOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [deleteableName, setDeleteableName] = useState<string>("");
  const [deleteableId, setDeleteableId] = useState<number>(0);
  const [formik, setFormik] = useState<FormikProps<IMasterList>>();
  const { data: updateMaster, updateableDate } = useAppSelector(
    (state) => state.UpdateMaster
  );
  const { data: addMaster } = useAppSelector((state) => state.AddMaster);
  const { data: deleteMasterData } = useAppSelector(
    (state) => state.DeleteMaster
  );
  const { data: unlockMasterData } = useAppSelector(
    (state) => state.UnlockMaster
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const isUpdateable =
    updateableDate && Object.keys(updateableDate).length !== 0;
  const column: Columns = [
    {
      field: "index",
      renderHeader: () => <b>S.No</b>,
      flex: 1,
      headerAlign: "center",
      align: "center",
      type: "number",
      maxWidth: 90,
      sortable: false,
    },
    {
      field: "first_name",
      renderHeader: () => <b>Name</b>,
      flex: 1,
      type: "string",
      maxWidth: 250,
    },
    {
      field: "email",
      renderHeader: () => <b>Email</b>,
      flex: 1,
      type: "string",
    },
    {
      field: "phone",
      renderHeader: () => <b>Contact Number</b>,
      flex: 1,
      type: "string",
      maxWidth: 190,

      // valueGetter: (params: GridValueGetterParams<IMasterList>) =>
      //   params.row.role?.role_name,
    },
    {
      field: "status",
      renderHeader: () => <b>Status</b>,
      flex: 1,
      type: "boolean",
      maxWidth: 190,

      renderCell: (params) => (
        <div>{params.value ? "Active" : "In-active"}</div>
      ),
      cellClassName: (params) => (params.value ? "active" : "inactive"),
    },
    {
      field: "otp_locked",
      renderHeader: () => <b>OTP Status</b>,
      flex: 1,
      type: "boolean",
      maxWidth: 150,
      renderCell: (params) => (
        <div>{params.value ? "Locked" : "Active"}</div>
      ),
      cellClassName: (params) => (params.value ? "otp-locked" : "otp-active"),
    },
    {
      field: "action",
      renderHeader: () => <b>Action</b>,
      flex: 1,
      type: "actions",
      getActions: (params: GridRowParams<any>) => {
        const actions = [];
        
        // Add Unlock button only if OTP is locked
        if (params.row.otp_locked) {
          actions.push(
            <GridActionsCellItem
              key="Unlock"
              label="Unlock OTP"
              icon={<LockOpen className="action_icons" />}
              onClick={() => {
                if (params.row.phone) {
                  dispatch(unlockMaster({ phone: params.row.phone }));
                }
              }}
            />
          );
        }
        
        // Add Delete button
        actions.push(
          <GridActionsCellItem
            key="Delete"
            label="Delete"
            icon={<Delete className="action_icons" />}
            onClick={() => {
              setDeleteOpen(true);
              setDeleteableName(params.row.name ?? "");
              setDeleteableId(params.row.id!);
            }}
          />
        );
        
        return actions;
      },
      cellClassName: "action_cell",
    },
  ];

  useEffect(() => {
    dispatch(getMaster({ page, pageSize, sortField, sortOrder }));
  }, [
    page,
    pageSize,
    sortField,
    sortOrder,
    dispatch,
    updateMaster,
    addMaster,
    deleteMasterData,
    unlockMasterData,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearAddMasterState());
      dispatch(clearUpdateMasterState());
      dispatch(clearDeleteMaster());
      dispatch(clearUnlockMaster());
    };
  }, []);

  // Handle unlock success - refresh the list
  useEffect(() => {
    if (unlockMasterData?.response?.status === true || unlockMasterData?.response?.responseCode === 200) {
      dispatch(getMaster({ page, pageSize, sortField, sortOrder }));
      dispatch(clearUnlockMaster());
    }
  }, [unlockMasterData, dispatch, page, pageSize, sortField, sortOrder]);

  const data1 = data?.response?.data;
  const masterData = Array.isArray(data1)
    ? data1.map((master, index) => ({
        ...master,
        id: master._id,
        index: page * pageSize + (index + 1), // ✅ fixed serial number
      }))
    : [];

  const masterForm = (data: FormikProps<IMasterList>) => {
    setFormik(data);
  };

  return (
    <>
      <Table
        rows={masterData ?? []}
        columns={column}
        rowCount={(data?.response as any)?.total ?? 0}
        onPageChange={(newPage, newPageSize) => {
          setPage(newPage);
          setPageSize(newPageSize);
        }}
        onSortChange={(field, order) => {
          if (field && order) {
            setSortField(field);
            setSortOrder(order);
          }
        }}>
        <CustomToolbar>
          <h6>Users</h6>
          <TableSearch />
          {/* <ButtonField
              className="add_button"
              onClick={() => setOpen(true)}>
              <SvgIcon
                component={Add}
                inheritViewBox
              />
              Add New
            </ButtonField> */}
        </CustomToolbar>
      </Table>

      <Dialog
        open={open}
        dialogClassName="master_dialog"
        dialogTitleClassName="master_dialog_title"
        Dialog_Title={
          <>
            {isUpdateable ? "Edit" : "Add New User"}
            <IconButtonField onClick={() => setOpen(false)}>
              <SvgIcon
                component={Cancel}
                inheritViewBox
              />
            </IconButtonField>
          </>
        }
        Dialog_Content={<MasterForm masterForm={masterForm} />}
        dialogActionClassName="master_dialog_action"
        Dialog_Actions={
          <>
            <ButtonField
              className="cancel_button"
              onClick={() => setOpen(false)}>
              Cancel
            </ButtonField>
            <ButtonField
              className="add_button"
              onClick={() => formik?.handleSubmit()}>
              {isUpdateable ? "Update" : "Save"}
            </ButtonField>
          </>
        }
      />

      <ResponseDialog
        response={addMaster}
        parentDialog={setOpen}
      />
      <ResponseDialog
        response={updateMaster}
        parentDialog={setOpen}
      />

      <DeleteDialog
        deleteableName={deleteableName}
        open={deleteOpen}
        setOpen={setDeleteOpen}
        deleteFunction={() => dispatch(deleteMaster(deleteableId))}
        response={deleteMasterData}
      />

      <ResponseDialog
        response={unlockMasterData}
        parentDialog={() => dispatch(clearUnlockMaster())}
      />
      <FailureResponseDialog response={unlockMasterData} />
    </>
  );
};

export default Master;
